import { EditorMode } from "shared/enums/EditorModeEnum";
import { ValidationGroup } from "devextreme-react/validation-group";
import React from "react";
import { AppDispatch } from "store/store";
import { createNewProduct, updateProduct } from "store/slices/ProductManagementSlice";
import { connect } from "react-redux";
import EditorActionFooter from "components/common/EditorActionFooter";
import FormGroup from "components/common/FormGroup";
import Checkbox from "components/common/controls/Checkbox";
import InputField from "components/common/controls/InputField";
import { Component } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import EditorModal from "../products/EditorModal";
import ProductRequest from "interfaces/request/ProductRequest";
import AdminProduct from "interfaces/response/AdminProduct";
import PromineoNumberBox from "components/common/controls/PromineoNumberBox";

interface Props {
  product?: AdminProduct;
  dispatch?: AppDispatch;
  mode: EditorMode;
  onClose: () => void;
}

interface State {
  isLoading: boolean;
  productRequest: ProductRequest;
}

export class ProductEditorPopup extends Component<Props, State> {
  isInEditMode = this.props.mode === EditorMode.Edit;
  productId = this.props.product ? this.props.product.id : -1; // For New Product we are considering productId = -1 //
  modalTitle = this.isInEditMode ? "Edit Product" : "Create New Product";
  applyButtonCaption = this.isInEditMode ? "SAVE CHANGES" : "Create New";
  cancelButtonCaption = "Cancel";
  isActiveFieldName = "isActive";
  isKeyRequiredFieldName = "isKeyRequired";
  isDefaultProductFieldName = "isDefaultProduct";
  actionPriceFieldName = "actionPrice";
  actionPriceUnitFieldName = "actionPriceUnit";

  formValidationGroupRef = React.createRef<any>();

  state: Readonly<State> = {
    isLoading: false,
    productRequest: {
      code: this.props.product ? this.props.product.code : undefined,
      name: this.props.product ? this.props.product.name : "",
      description: this.props.product ? this.props.product.description : "",
      apiUrl: this.props.product ? this.props.product.apiUrl : "",
      isKeyRequired: this.props.product ? this.props.product.isKeyRequired : false,
      isActive: this.props.product ? this.props.product.isActive : true,
      isDefaultProduct: this.props.product ? this.props.product.isDefaultProduct : false,
      actionPrice: this.props.product?.actionPrice,
      actionPriceUnit: this.props.product?.actionPriceUnit,
      stripeId : this.props.product ? this.props.product.stripeId : ""
    }
  }

  handleInputChanged = (eventArgs: any) => {
    this.handleChange(eventArgs.event.target.value, eventArgs.event.target.name);
  }

  handleChange = (value: any, fieldName: string) => {
    this.setState({ 
      productRequest: {
        ...this.state.productRequest,
        [fieldName]: value
      }
    });
  }

  handleSubmit = () => {
    var validationResult = this.formValidationGroupRef.current.instance.validate();
    if (!validationResult.isValid) {
      return;
    }

    let { code, name, description, apiUrl, isKeyRequired, isActive, isDefaultProduct, actionPrice, actionPriceUnit, stripeId } = this.state.productRequest;

    if (actionPrice && !actionPriceUnit) {
      actionPriceUnit = 1;
    }

    const productDetails: ProductRequest = {
      code,
      name,
      description,
      apiUrl,
      isKeyRequired,
      isActive,
      isDefaultProduct,
      actionPrice,
      actionPriceUnit,
      stripeId
    }

    this.setState({ isLoading: true });
    if (this.isInEditMode) {
      this.props.dispatch!(
        updateProduct({
          productId: this.productId,
          productRequest: productDetails,
        })
      )
      .then(unwrapResult)
      .then(() => {
        this.props.onClose?.();
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
    }
    else {
      this.props.dispatch!(createNewProduct(productDetails))
        .then(unwrapResult)
        .then(() => {
          this.props.onClose?.();
        })
        .finally(() => {
          this.setState({ isLoading: false });
        });
    }
  };

  render() {
    return (
      <>
        <EditorModal title={this.modalTitle} onClose={() => this.props.onClose?.()} isLoading={this.state.isLoading}>
          <div>
            <form>
              <ValidationGroup ref={this.formValidationGroupRef}>
                <div className="grid gap-x-3 gap-y-4 grid-cols-2">
                  <div className="col-span-2">
                    <FormGroup label="Name">
                      <InputField
                        placeholder="Name"
                        id="name"
                        label="Name"
                        name="name"
                        maxLength={255}
                        defaultValue={this.state.productRequest.name}
                        required={true}
                        onChange={this.handleInputChanged}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-span-2">
                    <FormGroup label="Code">
                      <InputField
                        placeholder="product code number"
                        id="code"
                        label="Code"
                        name="code"
                        defaultValue={this.state.productRequest.code}
                        required={true}
                        onChange={this.handleInputChanged}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-span-2">
                    <FormGroup label="Description">
                      <InputField
                        placeholder="Description"
                        id="description"
                        label="Description"
                        name="description"
                        maxLength={2048}
                        defaultValue={this.state.productRequest.description}
                        required={true}
                        onChange={this.handleInputChanged}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-span-2">
                    <FormGroup label="ApiUrl">
                      <InputField
                        placeholder="Api Url"
                        id="apiUrl"
                        label="ApiUrl"
                        name="apiUrl"
                        maxLength={2048}
                        defaultValue={this.state.productRequest.apiUrl}
                        onChange={this.handleInputChanged}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-span-2">
                    <FormGroup label="Stripe Id">
                      <InputField
                        placeholder="Stripe Id"
                        id="stripeId"
                        label="Stripe Id"
                        name="stripeId"
                        maxLength={255}
                        defaultValue={this.state.productRequest.stripeId}
                        onChange={this.handleInputChanged}
                      />
                    </FormGroup>
                  </div>
                  <FormGroup label="Action Price">
                    <PromineoNumberBox
                      placeholder="Action Price"
                      id="actionPrice"
                      label="Action Price"
                      name="actionPrice"
                      min={0}
                      defaultValue={this.state.productRequest.actionPrice}
                      onChange={(e : any) => {
                        this.handleChange(e.event.target.value, this.actionPriceFieldName);
                      }}
                    />
                  </FormGroup>
                  <FormGroup label="Action Price Unit">
                    <PromineoNumberBox
                      placeholder="Action Price Unit"
                      id="actionPriceUnit"
                      label="Action Price Unit"
                      name="actionPriceUnit"
                      min={0}
                      format= "#"
                      defaultValue={this.state.productRequest.actionPriceUnit}
                      onChange={(e : any) => {
                        this.handleChange(e.event.target.value, this.actionPriceUnitFieldName);
                      }}
                    />
                  </FormGroup>
                    <FormGroup label="Status">
                      <Checkbox
                        className="w-28"
                        name="isActive"
                        text="Active"
                        label="Active"
                        defaultValue={this.state.productRequest.isActive}
                        onValueChange={(e : any) => {
                          this.handleChange(e, this.isActiveFieldName);
                        }}
                      />
                    </FormGroup>
                    <FormGroup label="Key Required">
                      <Checkbox
                        className="w-28"
                        name="isKeyRequired"
                        text="Yes"
                        label="isKeyRequired"
                        defaultValue={this.state.productRequest.isKeyRequired}
                        onValueChange={(e : any) => {
                          this.handleChange(e, this.isKeyRequiredFieldName);
                        }}
                      />
                    </FormGroup>
                    <FormGroup label="Default Product">
                      <Checkbox
                        className="w-28"
                        name="isDefaultProduct"
                        text="Yes"
                        label="isDefaultProduct"
                        defaultValue={this.state.productRequest.isDefaultProduct}
                        onValueChange={(e : any) => {
                          this.handleChange(e, this.isDefaultProductFieldName);
                        }}
                      />
                    </FormGroup>
                </div>
              </ValidationGroup>
              <div className="mt-6">
                <EditorActionFooter 
                  applyButtonCaption={this.applyButtonCaption}
                  cancelButtonCaption={this.cancelButtonCaption}
                  onApply={this.handleSubmit}
                  onCancel={() => this.props.onClose?.()}
                />
              </div>
            </form>
          </div>
        </EditorModal>
      </>
    );
  }
}

export default connect()(ProductEditorPopup);
